import React, { useState, useEffect } from 'react';
import { MessageCircle, ThumbsUp } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { ForumComment } from '../../types/forum';
import { useForumComment } from '../../hooks/useForumComment';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

interface CommentListProps {
  comments: ForumComment[];
  onReply: (commentId: string) => void;
  maxDepth?: number;
}

const CommentList: React.FC<CommentListProps> = ({
  comments,
  onReply,
  maxDepth = 3 // Limit nesting to 3 levels by default
}) => {
  const { likeComment } = useForumComment();
  const { user } = useAuth();
  const [likedComments, setLikedComments] = useState<Record<string, boolean>>({});
  const [likesCount, setLikesCount] = useState<Record<string, number>>({});
  const [isLoadingLikes, setIsLoadingLikes] = useState(false);

  // Initialize likes count
  useEffect(() => {
    const initialLikesCount = comments.reduce((acc, comment) => {
      acc[comment.id] = comment.likes_count || 0;
      return acc;
    }, {} as Record<string, number>);
    setLikesCount(initialLikesCount);
  }, [comments]);

  // Load user's liked comments
  useEffect(() => {
    const loadLikedComments = async () => {
      if (!user) {
        setLikedComments({});
        return;
      }

      try {
        setIsLoadingLikes(true);
        const commentIds = comments.map(c => c.id);
        
        const { data, error } = await supabase
          .from('comment_likes')
          .select('comment_id')
          .eq('user_id', user.id)
          .in('comment_id', commentIds);

        if (error) throw error;

        const liked = (data || []).reduce((acc, like) => {
          acc[like.comment_id] = true;
          return acc;
        }, {} as Record<string, boolean>);
        
        setLikedComments(liked);
      } catch (error) {
        console.error('Error loading liked comments:', error);
      } finally {
        setIsLoadingLikes(false);
      }
    };

    loadLikedComments();
  }, [user, comments]);

  const handleLike = async (commentId: string) => {
    if (!user || isLoadingLikes) return;

    try {
      // Optimistic update
      const isLiked = likedComments[commentId];
      setLikedComments(prev => ({
        ...prev,
        [commentId]: !isLiked
      }));
      setLikesCount(prev => ({
        ...prev,
        [commentId]: (prev[commentId] || 0) + (isLiked ? -1 : 1)
      }));

      await likeComment(commentId);
    } catch (error) {
      // Revert on error
      console.error('Failed to like comment:', error);
      setLikedComments(prev => ({
        ...prev,
        [commentId]: !prev[commentId]
      }));
      setLikesCount(prev => ({
        ...prev,
        [commentId]: (prev[commentId] || 0) + (likedComments[commentId] ? 1 : -1)
      }));
    }
  };

  const renderComment = (comment: ForumComment, depth = 0) => {
    const replies = comments.filter(c => c.parent_id === comment.id);
    const canNest = depth < maxDepth;

    // Handle deleted accounts
    const isDeletedAccount = comment.user?.is_deleted;
    const displayName = isDeletedAccount ? 'Deleted Account' : (comment.user?.display_name || 'Anonymous User');
    const avatarUrl = isDeletedAccount 
      ? '/images/deleted-account-avatar.png' 
      : (comment.user?.avatar_url || `https://api.dicebear.com/7.x/identicon/svg?seed=${comment.user_id}`);
    const isLiked = likedComments[comment.id];

    return (
      <div key={comment.id} className={`mb-4 ${depth > 0 ? 'ml-8' : ''}`}>
        <div className="bg-background-secondary p-4 rounded-lg">
          <div className="flex items-start gap-4 mb-2">
            {/* Avatar */}
            <img
              src={avatarUrl}
              alt={displayName}
              className="w-10 h-10 rounded-lg border border-gray-800"
              key={avatarUrl}
            />
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <span className={`font-medium ${isDeletedAccount ? 'text-content-secondary' : 'text-content-primary'}`}>
                  {displayName}
                </span>
                <span className="text-sm text-content-secondary">
                  {new Date(comment.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="prose prose-invert max-w-none mt-2">
                <ReactMarkdown
                  components={{
                    img: ({ node, ...props }) => (
                      <div className="max-w-full overflow-hidden">
                        <img
                          {...props}
                          className="max-w-full max-h-[500px] w-auto h-auto object-contain rounded-lg"
                          loading="lazy"
                        />
                      </div>
                    ),
                    a: ({ node, ...props }) => (
                      <a
                        {...props}
                        className="text-brand hover:text-brand-light"
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ),
                    p: ({ node, ...props }) => (
                      <p {...props} className="text-content-secondary" />
                    )
                  }}
                >
                  {comment.content}
                </ReactMarkdown>
              </div>
              <div className="flex items-center gap-4 mt-4">
                <button 
                  onClick={() => handleLike(comment.id)}
                  className={`flex items-center text-sm transition-colors ${
                    isLiked 
                      ? 'text-brand hover:text-brand-dark' 
                      : 'text-content-secondary hover:text-brand'
                  }`}
                  disabled={!user || isLoadingLikes}
                >
                  <ThumbsUp className={`w-4 h-4 mr-1 ${isLiked ? 'fill-current' : ''}`} />
                  {likesCount[comment.id] || 0}
                </button>
                {canNest && !isDeletedAccount && (
                  <button
                    onClick={() => onReply(comment.id)}
                    className="flex items-center text-sm text-content-secondary hover:text-brand"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    Reply
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Only render replies if we haven't reached max depth */}
        {canNest && replies.map(reply => renderComment(reply, depth + 1))}
        {!canNest && replies.length > 0 && (
          <div className="mt-2 text-sm text-content-secondary">
            {replies.length} more {replies.length === 1 ? 'reply' : 'replies'}
          </div>
        )}
      </div>
    );
  };

  const topLevelComments = comments.filter(c => !c.parent_id);

  return (
    <div className="space-y-4">
      {topLevelComments.map(comment => renderComment(comment))}
    </div>
  );
};

export default CommentList;